import { useLocation } from "react-router-dom";
import { useState } from "react";

import * as StateLib from "@/Utils/StateLib";
import { ExportModal, ExportModalMode } from "./ExportModal";
import { Button, Menu } from "@theorgchartdev/storybook";
import { useChartDocumentContext } from "@/providers/ChartDocumentProvider";
import { useDirectorySearchContext } from "@/providers/DirectorySearchProvider";
import { useReportingContext } from "@/providers/ReportsProvider";
import { useOptionalTabularDisplayContext } from "@/providers/TabularDisplayProvider";
import { appstr } from "@coreapp/utils/appstr";
import { Globals } from "@coreapp/CoreApp/Globals";
import { isInDirectory } from "@coreapp/utils/isInDirectory";
import { isInReporting } from "@coreapp/utils/isInReporting";
import { getFeatureAccess } from "@/Utils/Misc";
import { enumBrand, enumFeaturesList } from "@coreapp/constants";
import { gWhiteLabel } from "@coreapp/whitelabel/brand";
import UpgradeExport from "./UpgradeExport";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { isInPlanning } from "@coreapp/utils/isInPlanning";

const DoExport = target => {
  let wsMgr = StateLib.GetWSMgr();
  let pubValue = target.value;
  let pubScope = "Page";
  if (pubValue.includes("_")) {
    let pubValues = pubValue.split("_");
    pubValue = pubValues[0];
    pubScope = pubValues[1];
  }
  pubValue = pubValue.toUpperCase();
  wsMgr.PerformPublishCommand(pubValue, pubScope);
  const eventTracker = wsMgr.GetUsageTracker();
  eventTracker.ReportEvent("chart_mode.export.exported", { export_format: pubValue.toLowerCase() });
};

const exportDict = [
  {
    labelKey: appstr("Web Link"),
    value: "weblink",
    icon: "link"
  },
  {
    labelKey: appstr("Excel Spreadsheet"),
    modalMode: ExportModalMode.EXCEL,
    value: "excel",
    icon: "file-excel"
  },
  {
    labelKey: appstr("JPG Image"),
    modalMode: ExportModalMode.JPG,
    value: "jpg",
    icon: "file-image"
  },
  {
    labelKey: appstr("SVG Image"),
    modalMode: ExportModalMode.SVG,
    value: "svg",
    icon: "bezier-curve"
  },
  {
    labelKey: appstr("PNG Image"),
    modalMode: ExportModalMode.PNG,
    value: "png",
    icon: "file-image"
  },
  {
    labelKey: appstr("Powerpoint document"),
    modalMode: ExportModalMode.PPT,
    value: "ppt",
    icon: "file-powerpoint"
  },
  {
    labelKey: appstr("PDF Document"),
    modalMode: ExportModalMode.PDF,
    value: "pdf",
    divider: true,
    icon: "file-pdf"
  },
  {
    labelKey: appstr("Print Current Screen"),
    value: "pdf_Screen",
    icon: "file-pdf",
    clickHandler: DoExport
  }
];

const ExportMenu = () => {
  const location = useLocation();
  const { chartDocument } = useChartDocumentContext();
  const { dirSearchState } = useDirectorySearchContext();
  const { reportSearchState, currentOpenReport } = useReportingContext();
  const { canExport, showPricingAndPackaging } = usePermissionCheck();
  const tabularDisplayContext = useOptionalTabularDisplayContext();
  const isTabularDisplay = tabularDisplayContext?.isTabularDisplay ?? false;
  const [exportModalMode, setExportModalMode] = useState(null);

  if (!Globals._uiConfig.fCanPublish) return;

  const isIsolvedUpgradeStyling =
    !canExport && gWhiteLabel === enumBrand.isolved && showPricingAndPackaging;

  if (!canExport && !isIsolvedUpgradeStyling) {
    return Globals._uiConfig.fPackaging ? <UpgradeExport /> : null;
  }

  const handleOpenModal = mode => () => {
    setExportModalMode(mode);
  };

  const handleCloseModal = () => {
    setExportModalMode(null);
  };

  const wsMgr = StateLib.GetWSMgr();

  const BuildExportOptions = () => {
    let exportOptions = [];
    if (!StateLib.GetDoc()) return exportOptions;

    const uInfo = wsMgr.GetUserInfo();
    const inDirectory = isInDirectory();
    const inReporting = isInReporting();
    const isPlanningTableExport =
      isInPlanning() && chartDocument?.state?.IsPositionDocument?.() === true && isTabularDisplay;
    const outputExcelTableOnly = inDirectory || inReporting || isPlanningTableExport;

    for (const exportOption of exportDict) {
      const newOption = {
        ...exportOption,
        dataTestId: `export-menu-${exportOption.value}`,
        label: appstr(exportOption.labelKey)
      };
      const pubType = newOption.value;
      // PDF-only export mode (e.g. NIH portal): offer only the PDF document option.
      // `value` is a string literal ("pdf"), matching the switch cases below — not ExportModalMode.
      if (Globals._uiConfig?.fPDFExportOnly && pubType !== "pdf") continue;
      switch (pubType) {
        case "svg":
        case "jpg":
        case "png":
          if (outputExcelTableOnly) continue;
          if (!getFeatureAccess(enumFeaturesList.exportPNG)) {
            newOption.disabled = true;
          }
          newOption.clickHandler = handleOpenModal(newOption.modalMode);
          break;
        case "pdf":
        case "ppt":
          if (outputExcelTableOnly) continue;
          newOption.clickHandler = handleOpenModal(newOption.modalMode);
          if (!getFeatureAccess(enumFeaturesList.exportPDFPPT)) {
            newOption.disabled = true;
          }
          break;
        case "excel":
          if (!Globals._uiConfig.fCanExportExcel || !uInfo.CanExportExcel()) continue;
          newOption.clickHandler = handleOpenModal(newOption.modalMode);
          if (!getFeatureAccess(enumFeaturesList.exportExcel)) {
            newOption.disabled = true;
          }
          break;
        case "weblink":
          if (outputExcelTableOnly) continue;
          newOption.disabled = true;
          continue;
        default:
          newOption.clickHandler = DoExport;
          if (outputExcelTableOnly) continue;
      }
      exportOptions.push(newOption);
    }
    return exportOptions;
  };

  const gemExportTrigger = (
    <Button
      modifier="primary"
      color="brand"
      iconAlignEnd={false}
      iconProps={{ name: "gem" }}
      disabled={chartDocument.state == null || Globals._uiConfig.InBoxLayoutEditorMode()}
      ariaLabel={appstr("View and select chart export options")}
    >
      {appstr("Export")}
    </Button>
  );

  return (
    <>
      <Menu
        bold
        buttonSize={"medium"}
        {...(isIsolvedUpgradeStyling
          ? { trigger: gemExportTrigger }
          : {
              triggerProps: {
                modifier: "primary",
                ariaLabel: appstr("View and select chart export options")
              }
            })}
        label={appstr("Export")}
        disabled={
          !canExport || chartDocument.state == null || Globals._uiConfig.InBoxLayoutEditorMode()
        }
        options={canExport ? BuildExportOptions() : []}
        position={"bottom-end"}
        listWidth={"medium"}
        maxHeight={600}
        dataTestId={isIsolvedUpgradeStyling ? "header-export-upgrade-menu" : "header-export-menu"}
      />
      <ExportModal mode={exportModalMode} onClose={handleCloseModal} />
    </>
  );
};

export default ExportMenu;
